import { Button, Input } from "@aix/ui"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { BookOpen, Clock, Search, Tag, Video } from "lucide-react"
import { useState } from "react"
import { trpc } from "../../lib/trpc"

export default function JourneysListScreen() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { search?: string; tags?: string[] }

  const [searchQuery, setSearchQuery] = useState(search.search || "")
  const [selectedTags, setSelectedTags] = useState<string[]>(search.tags || [])

  // Fetch journeys
  const { data: journeys, isLoading } = trpc.videoOnboarding.listJourneys.useQuery({
    search: searchQuery || undefined,
    tags: selectedTags.length > 0 ? selectedTags : undefined,
  })

  // Fetch all videos to extract unique tags
  const { data: videos } = trpc.videoOnboarding.listVideos.useQuery({ limit: 100 })
  const allTags = Array.from(new Set(videos?.flatMap((v) => v.tags || []) || []))

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    navigate({
      to: "/onboarding/journeys",
      // @ts-ignore
      search: (s) => ({ ...s, search: query || undefined }),
    })
  }

  const toggleTag = (tag: string) => {
    const newTags = selectedTags.includes(tag) ? selectedTags.filter((t) => t !== tag) : [...selectedTags, tag]
    setSelectedTags(newTags)
    navigate({
      to: "/onboarding/journeys",
      // @ts-ignore
      search: (s) => ({ ...s, tags: newTags.length > 0 ? newTags : undefined }),
    })
  }

  const handleEnroll = async (journeyId: string) => {
    try {
      await enrollMutation.mutateAsync({ journeyId })
      navigate({
        to: "/onboarding/journey/$journeyId",
        params: { journeyId },
      })
    } catch (error) {
      console.error("Failed to enroll:", error)
    }
  }

  const enrollMutation = trpc.videoOnboarding.enrollInJourney.useMutation()

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">Learning Journeys</h1>
          <p className="text-muted-foreground">Browse and enroll in video training courses</p>
        </div>

        {/* Search & Filters */}
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search journeys..."
              className="pl-10"
            />
          </div>

          {/* Tag Filters */}
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Tag className="w-4 h-4" />
                Filter:
              </span>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedTags.includes(tag)
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground hover:bg-accent"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading journeys...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && journeys?.length === 0 && (
          <div className="text-center py-12 bg-secondary rounded-lg">
            <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No journeys found</h3>
            <p className="text-muted-foreground">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Journey Grid */}
        {!isLoading && journeys && journeys.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {journeys.map((journey) => (
              <div
                key={journey.id}
                className="bg-secondary rounded-lg p-6 border border-border hover:border-primary transition-all cursor-pointer group"
                onClick={() =>
                  navigate({
                    to: "/onboarding/journey/$journeyId",
                    params: { journeyId: journey.id },
                  })
                }
              >
                {/* Icon & Title */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="text-4xl">{journey.icon || "📚"}</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-primary group-hover:text-accent transition-colors">
                      {journey.name}
                    </h3>
                  </div>
                </div>

                {/* Description */}
                {journey.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{journey.description}</p>
                )}

                {/* Meta Info */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Video className="w-4 h-4" />
                    <span>{journey.videoCount || 0} videos</span>
                  </div>
                  {journey.estimatedMinutes && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{journey.estimatedMinutes} min</span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {journey.tags && journey.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {journey.tags.map((tag) => (
                      <span key={tag} className="px-2 py-1 bg-background rounded text-xs text-muted-foreground">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* CTA Button */}
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleEnroll(journey.id)
                  }}
                  className="w-full"
                  loading={enrollMutation.isLoading}
                >
                  View Journey
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
